import { getFormConfigRegistry } from '@risksmart-app/shared/forms/formConfigRegistry';
import _ from 'lodash';
import type { MutableRefObject } from 'react';
import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useModulesStore } from 'src/context/moduleContext';
import { useCustomDatasourceHelpers } from 'src/pages/custom-datasources/useCustomDatasourceHelpers';
import { useT1CategoryConditionSource } from 'src/pages/risks/useT1CategoryConditionSource';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { AdditionalConditionField } from '../../edit-field-modal/formRegistryService';
import { useRiskSmartForm } from '../customisable-form/RiskSmartFormContext';
import { useCustomisableFormDataContext } from '../customisable-form-data/CustomisableFormDataContext';
import { ConditionalFieldsContext } from './ConditionalFieldsContext';
import {
  buildConditionGraph,
  buildConditionSchemaContext,
  buildFieldConditionGraph,
  getEffectiveFieldConditions,
  getHiddenFields,
  getHiddenSections,
} from './conditionsGraph';
import type { ExternalConditionSources } from './externalConditionSources';

/**
 * Keeps track of which fields are conditionally hidden.
 * Note, needs to be used with a react hook form FormProvider as it needs access to the forms values
 *
 * `additionalConditionFields` lets the form-config preview register
 * session-only fields as condition sources so rules referencing them evaluate
 * before the field is persisted. Live forms omit it.
 *
 * `externalSourcesRef` is the reverse direction: the sources this provider
 * resolves itself (CUS-242's T1 Category) published back to `FormContext`,
 * whose save handler re-runs the same evaluation over the submitted payload.
 * See {@link ExternalConditionSources} for why that path cannot be left blind.
 */
export const ConditionalFieldsProvider = ({
  children,
  additionalConditionFields,
  externalSourcesRef,
}: PropsWithChildren<{
  additionalConditionFields?: AdditionalConditionField[];
  externalSourcesRef?: MutableRefObject<ExternalConditionSources>;
}>) => {
  const [hiddenFields, setHiddenFields] = useState<Set<string>>(new Set());
  const [hiddenSections, setHiddenSections] = useState<Set<string>>(new Set());
  const customisableData = useCustomisableFormDataContext();
  const { formFieldConfigurations } = customisableData;
  // TODO: possibly get parent type by other means to remove dependency on useRiskSmartForm
  const { parentType } = useRiskSmartForm();

  const { watch } = useFormContext();
  const modules = useModulesStore((s) => s.modules);
  const currentValues = watch();
  const formSectionsEnabled = useIsFeatureFlagEnabled('dynamic_forms');

  const formConfig = parentType
    ? getFormConfigRegistry(formSectionsEnabled, modules)[parentType]
    : undefined;

  const { t } = useTranslation(['common'], { keyPrefix: 'risks.columns' });

  // CUS-242 — T1 Category is a condition source that is not a form field: it is
  // the record's nearest Tier-1 ancestor. It therefore arrives as both a
  // synthetic field declaration and an externally-resolved value, and is absent
  // entirely unless the `trpc` flag is on (the walk needs columns only the V3
  // read returns).
  const {
    additionalField: t1CategoryField,
    conditionValues: t1CategoryValues,
  } = useT1CategoryConditionSource({
    currentValues,
    formId: parentType,
    label: t('t1_category'),
  });

  const effectiveAdditionalFields = useMemo(
    () =>
      t1CategoryField
        ? [...(additionalConditionFields ?? []), t1CategoryField]
        : additionalConditionFields,
    [additionalConditionFields, t1CategoryField]
  );

  // The evaluator sees the form's own values plus the externally-resolved T1
  // Category. Named separately rather than shadowing `currentValues`, so the
  // raw form values stay available and this stops colliding with unrelated
  // edits to the line above.
  const effectiveValues = useMemo(
    () =>
      t1CategoryValues
        ? { ...currentValues, ...t1CategoryValues }
        : currentValues,
    [currentValues, t1CategoryValues]
  );

  // Publish the resolved sources for the save-time re-evaluation in
  // `FormContext`. An effect rather than a render-time assignment so a
  // discarded StrictMode render cannot leave a value behind; the save is a
  // separate user event, by which point this has committed.
  useEffect(() => {
    if (!externalSourcesRef) {
      return;
    }
    externalSourcesRef.current = {
      fields: effectiveAdditionalFields,
      values: t1CategoryValues,
    };

    return () => {
      externalSourcesRef.current = {};
    };
  }, [externalSourcesRef, effectiveAdditionalFields, t1CategoryValues]);

  const fieldConditionGraph = useMemo(
    () =>
      buildFieldConditionGraph(
        getEffectiveFieldConditions(
          formFieldConfigurations,
          formConfig,
          formSectionsEnabled
        )
      ),
    [formFieldConfigurations, formConfig, formSectionsEnabled]
  );

  // Fields AND sections. `getHiddenSections` walks this to inherit the field
  // cascade; `getHiddenFields` keeps the field-only graph above, because adding
  // section vertices to its ancestor walk would let a section hide a field,
  // which is not a relationship this model has.
  const conditionGraph = useMemo(
    () =>
      buildConditionGraph(
        getEffectiveFieldConditions(
          formFieldConfigurations,
          formConfig,
          formSectionsEnabled
        ),
        customisableData.formSections ?? []
      ),
    [
      formFieldConfigurations,
      formConfig,
      formSectionsEnabled,
      customisableData.formSections,
    ]
  );

  const helpers = useCustomDatasourceHelpers();
  const { customAttributeSchema } = customisableData;

  // Memoize the static schema context so filteringProps and freeTextKeys are not
  // recomputed on every form-value change (they depend on schema, not on field values).
  const schemaContext = useMemo(
    () =>
      parentType
        ? buildConditionSchemaContext({
            formId: parentType,
            customAttributeSchema,
            helpers,
            formSectionsEnabled,
            modules,
            additionalConditionFields: effectiveAdditionalFields,
          })
        : undefined,
    [
      parentType,
      customAttributeSchema,
      helpers,
      formSectionsEnabled,
      modules,
      effectiveAdditionalFields,
    ]
  );

  useEffect(() => {
    if (!parentType) {
      return;
    }

    const hiddenFieldsLatest = getHiddenFields({
      formId: parentType,
      customisableData,
      fieldConditionGraph,
      currentValues: effectiveValues,
      helpers,
      modules,
      additionalConditionFields: effectiveAdditionalFields,
      formSectionsEnabled,
      schemaContext,
    });

    const hiddenSectionsLatest = getHiddenSections({
      formId: parentType,
      customisableData,
      currentValues: effectiveValues,
      helpers,
      formSectionsEnabled,
      modules,
      additionalConditionFields: effectiveAdditionalFields,
      schemaContext,
      conditionGraph,
      // Computed immediately above from the same values, so the cascade sees
      // this render's hidden set rather than the previous one held in state.
      hiddenFields: hiddenFieldsLatest,
    });

    setHiddenFields((prev) => {
      if (_.isEqual(prev, hiddenFieldsLatest)) {
        return prev;
      }

      return hiddenFieldsLatest;
    });

    setHiddenSections((prev) => {
      if (_.isEqual(prev, hiddenSectionsLatest)) {
        return prev;
      }

      return hiddenSectionsLatest;
    });
  }, [
    fieldConditionGraph,
    conditionGraph,
    parentType,
    customisableData,
    effectiveValues,
    helpers,
    modules,
    effectiveAdditionalFields,
    formSectionsEnabled,
    schemaContext,
  ]);

  return (
    <ConditionalFieldsContext.Provider value={{ hiddenFields, hiddenSections }}>
      {children}
    </ConditionalFieldsContext.Provider>
  );
};
